import React, { useEffect, useState } from "react";
import Api from "../../../config/Api";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { FaCheck } from "react-icons/fa";
import { BsEyeFill } from "react-icons/bs";
import StatusBadge from "../Application/Components/StatusBadge";
import ModalBox from "../../Components/Widget/ModalBox";
import Table from "../../Components/Widget/Table";
import Loader from "../../Components/Widget/Loader";
import TooltipWidget from "../../Components/Widget/TooltipWidget";

const BulkApprovalApplications = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const [isLoading, setIsLoading] = useState(false);
  const [application, setApplication] = useState([]);
  const [isApplicationLoaded, setIsApplicationLoaded] = useState(false);
  const [filter, setFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [perPage, setPerPage] = useState(200);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [selectedApplicationIds, setSelectedApplicationIds] = useState([]);
  const [selectedData, setSelectedData] = useState({
    id: "",
  });
  const [isModalShow, setIsModalShow] = useState(false);
  const admin = useSelector((state) => JSON.parse(state.authentication.admin));

  const modalClose = () => {
    setIsModalShow(false);
    reset({
      name: admin?.full_name || "",
    });
    setSelectedData({
      id: "",
    });
  };

  const modalShow = (id) => {
    setIsModalShow(true);
    setSelectedData({
      id: String(id),
    });
  };

  const getApplication = async (
    status_id,
    page = 1,
    search = "",
    pageSize = perPage
  ) => {
    try {
      setIsLoading(true);
      const body = {
        status_id,
        page_no: page,
        per_page: pageSize,
      };

      if (search.trim()) {
        body.search = search.trim();
      }

      const res = await Api("application_list", "POST", body, true);

      if (res.data.status === 1) {
        const applicationData = Array.isArray(res.data.data) ? res.data.data : [];
        const paginationData =
          res.data.pagination &&
          typeof res.data.pagination === "object"
            ? res.data.pagination
            : res.data &&
              typeof res.data === "object" &&
              res.data.page
            ? {
                page_no: res.data.page,
                per_page: res.data.per_page,
                total_records: res.data.total,
              }
            : null;

        setApplication(applicationData);

        if (paginationData) {
          setCurrentPage(paginationData.page_no || page);
          setTotalRecords(paginationData.total_records || 0);
          setPerPage(paginationData.per_page || pageSize);
        } else {
          const total = res.data.total || res.data.count || applicationData.length;
          setCurrentPage(page);
          setTotalRecords(total);
          setPerPage(pageSize);
        }

        setIsApplicationLoaded(true);
      } else {
        toast.error(res.data.message, {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      }
    } catch (error) {
      toast.error(error?.data?.message || "Something went wrong!", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchInput = (value) => {
    setFilter(value);

    if (value.trim() === "") {
      setCurrentPage(1);
      setResetPaginationToggle((prev) => !prev);
      getApplication([5], 1, "", perPage);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setResetPaginationToggle((prev) => !prev);
    getApplication([5], 1, filter, perPage);
  };

  useEffect(() => {
    getApplication([5]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (admin) {
      reset({
        name: admin.full_name,
      });
    }
  }, [admin, reset]);

  const onSubmit = async (d) => {
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("application_id", selectedData.id);
      formData.append("name", d?.name);
      formData.append("remark", d?.remark);
      formData.append("status", d?.status);
      formData.append("sanction_letter_number", d?.sanction_letter_number);
      formData.append("sanction_date", d?.sanction_date);

      if (d?.document?.length > 0) {
        formData.append("document", d.document[0]);
      }

      const res = await Api("hod_application_approve", "POST", formData);

      if (res.data.status === 1 || res.data.status === 2) {
        toast.success(res.data.message, {
          position: toast.POSITION.BOTTOM_RIGHT,
        });

        const approvedIds = selectedData.id
          .split(",")
          .map((id) => id.trim())
          .filter(Boolean);

        setSelectedApplicationIds((prev) =>
          prev.filter((id) => !approvedIds.includes(id))
        );
        modalClose();
        getApplication([5], currentPage, filter, perPage);
      } else {
        toast.error(res.data.message, {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      }
    } catch (error) {
      toast.error(error.message, {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    {
      name: (
        <input
          type="checkbox"
          className="form-check-input"
          checked={
            application.length > 0 &&
            application.every((row) =>
              selectedApplicationIds.includes(String(row.id))
            )
          }
          onChange={(e) => {
            const visibleIds = application.map((row) => String(row.id));

            if (e.target.checked) {
              setSelectedApplicationIds((prev) => [
                ...new Set([...prev, ...visibleIds]),
              ]);
            } else {
              setSelectedApplicationIds((prev) =>
                prev.filter((id) => !visibleIds.includes(id))
              );
            }
          }}
        />
      ),
      cell: (row) => (
        <input
          type="checkbox"
          className="form-check-input"
          checked={selectedApplicationIds.includes(String(row.id))}
          onChange={(e) => {
            const rowId = String(row.id);

            if (e.target.checked) {
              setSelectedApplicationIds((prev) => [...new Set([...prev, rowId])]);
            } else {
              setSelectedApplicationIds((prev) =>
                prev.filter((id) => id !== rowId)
              );
            }
          }}
        />
      ),
      width: "70px",
      ignoreRowClick: true,
      allowOverflow: true,
    },
    {
      name: "#",
      cell: (_row, index) => (currentPage - 1) * perPage + index + 1,
      width: "70px",
    },
    {
      name: "Application ID",
      selector: (row) => row.application_id,
      sortable: true,
      wrap: true,
    },
    {
      name: "Applicant Passport Photo",
      selector: (row) => (
        <img
          src={row.applicant_default_img}
          alt="Applicant"
          style={{
            height: "70px",
            width: "100px",
            objectFit: "cover",
          }}
        />
      ),
    },
    {
      name: "Applicant Info",
      selector: (row) => (
        <>
          <div className="mb-1">{row.applicant_name}</div>
          <div>{row.mobile}</div>
        </>
      ),
    },
    {
      name: "District",
      selector: (row) => row.district?.district_name,
      sortable: true,
      wrap: true,
    },
    {
      name: "Status",
      selector: (row) => {
        if (row?.payment_status === "R") {
          return (
            <>
              <StatusBadge status={row.status} />
              <span
                style={{
                  backgroundColor: "red",
                  color: "white",
                  padding: "4px 6px",
                  borderRadius: "12px",
                  fontSize: "11px",
                }}
              >
                Failed
              </span>
            </>
          );
        }

        return <StatusBadge status={row.status} />;
      },
      sortable: true,
      wrap: true,
    },
    {
      name: "Action",
      selector: (row) => (
        <div className="d-flex gap-1">
          <TooltipWidget title="View SDRF">
            <Link className="btn btn-warning btn-sm" to={`/application/${window.btoa(row.id)}`}>
              <BsEyeFill />
            </Link>
          </TooltipWidget>
          <TooltipWidget title="Approve/Reject SDRF">
            <button
              type="button"
              className="btn btn-success btn-sm"
              onClick={() => {
                modalShow(row.id);
              }}
            >
              <FaCheck />
            </button>
          </TooltipWidget>
        </div>
      ),
      minWidth: "180px",
    },
  ];

  const handlePageChange = (page) => {
    setCurrentPage(page);
    getApplication([5], page, filter, perPage);
  };

  const handlePerRowsChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
    setResetPaginationToggle((prev) => !prev);
    getApplication([5], 1, filter, newPerPage);
  };

  const bulkApprove = () => {
    if (!selectedApplicationIds.length) {
      return;
    }

    modalShow(selectedApplicationIds.join(", "));
  };

  return (
    <>
      <div className="bg-white p-3 rounded shadow">
        <div className="mb-3">
          <div className="row g-2">
            <div className="col-lg-4 col-md-4">
              <h5 className="text-dark">Bulk Approval</h5>
            </div>

            <div className="col-lg-6 col-md-6">
              <input
                type="text"
                placeholder="Search Application"
                className="form-control"
                value={filter}
                onChange={(e) => handleSearchInput(e.target.value)}
                disabled={isLoading}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
              />
            </div>

            <div className="col-lg-2 col-md-2">
              <button
                type="button"
                className="btn btn-primary w-100"
                onClick={handleSearch}
                disabled={isLoading}
              >
                Search
              </button>
            </div>
          </div>
        </div>

        {isApplicationLoaded && (
          <div className="mb-3">
            <small className="text-muted">
              Showing {totalRecords === 0 ? 0 : (currentPage - 1) * perPage + 1} to{" "}
              {totalRecords === 0 ? 0 : Math.min(currentPage * perPage, totalRecords)} of{" "}
              {totalRecords} applications for bulk approval
            </small>
          </div>
        )}

        {selectedApplicationIds.length > 0 && (
          <div className="alert alert-info d-flex align-items-center p-2">
            <div className="h5 m-0 flex-grow-1">
              {selectedApplicationIds.length} item selected
            </div>
            <button
              type="button"
              className="btn btn-success"
              onClick={bulkApprove}
            >
              <FaCheck /> Bulk Approve
            </button>
          </div>
        )}

        <div>
          {isApplicationLoaded ? (
            <Table
              columns={columns}
              data={application}
              progressPending={isLoading}
              pagination
              paginationServer
              paginationTotalRows={totalRecords}
              paginationDefaultPage={currentPage}
              paginationPerPage={perPage}
              paginationRowsPerPageOptions={[20, 50, 100, 200]}
              paginationResetDefaultPage={resetPaginationToggle}
              onChangeRowsPerPage={handlePerRowsChange}
              onChangePage={handlePageChange}
            />
          ) : (
            <Loader />
          )}
        </div>
      </div>

      <ModalBox show={isModalShow} hide={modalClose} title="Bulk Approval">
        <div className="p-2">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row g-3">
              <div className="col-12">
                <label>
                  Name
                  <span className="text-danger fw-bold">*</span>
                </label>
                <input
                  className="form-control"
                  type="text"
                  {...register("name", {
                    required: true,
                  })}
                  disabled
                />
                {errors.name && <div className="error_line">Name is required</div>}
              </div>

              <div className="col-12">
                <label>
                  Comment/Remark
                  <span className="text-danger fw-bold">*</span>
                </label>
                <textarea
                  className="form-control"
                  {...register("remark", {
                    required: true,
                  })}
                  rows={5}
                />
                {errors.remark && (
                  <div className="error_line">Comment/Remark is required</div>
                )}
              </div>

              <div className="col-12">
                <label>Sanction Letter Number</label>
                <input
                  className="form-control"
                  type="text"
                  {...register("sanction_letter_number")}
                />
              </div>

              <div className="col-12">
                <label>Sanction Date</label>
                <input
                  className="form-control"
                  type="date"
                  max={new Date().toISOString().split("T")[0]}
                  {...register("sanction_date", {
                    required: "Sanction Date is required",
                    validate: (value) => {
                      const today = new Date();
                      const selectedDate = new Date(value);

                      today.setHours(0, 0, 0, 0);
                      selectedDate.setHours(0, 0, 0, 0);

                      const formatDate = (date) => {
                        const day = String(date.getDate()).padStart(2, "0");
                        const month = String(date.getMonth() + 1).padStart(2, "0");
                        const year = date.getFullYear();
                        return `${day}-${month}-${year}`;
                      };

                      return (
                        selectedDate <= today ||
                        `Sanction Date cannot be after ${formatDate(today)}`
                      );
                    },
                  })}
                />
                {errors.sanction_date && (
                  <div className="error_line">{errors.sanction_date.message}</div>
                )}
              </div>

              <div className="col-12">
                <label>Upload supporting file (if any)</label>
                <input
                  className="form-control"
                  type="file"
                  {...register("document")}
                />
              </div>

              <div className="col-12">
                <label>
                  Select Status <span className="text-danger fw-bold">*</span>
                </label>
                <select
                  className="form-select"
                  {...register("status", {
                    required: true,
                  })}
                >
                  <option value="">Select Status</option>
                  <option value="6">Final Approve</option>
                  <option value="7">Reject</option>
                </select>
                {errors.status && <div className="error_line">Status is required</div>}
              </div>

              <div className="col-12">
                <button
                  type="submit"
                  className="btn btn-primary px-5"
                  disabled={isLoading}
                >
                  Submit
                </button>
              </div>
            </div>
          </form>
        </div>
      </ModalBox>
    </>
  );
};

export default BulkApprovalApplications;
